import React, { useEffect, useState, useCallback } from "react";
import { Trophy, Medal, Users } from "lucide-react";
import { api } from "../lib/api";

/**
 * Live golf leaderboard. Polls /golf/leaderboard/{event_id} every 30s when
 * mounted. For Stableford ranks by points (desc), other formats by gross (asc).
 * Shows a separate teams panel for Ryder Cup / Scramble / Best Ball / Foursomes.
 */
export default function GolfLeaderboard({ eventId, eventKind = "match", refreshSignal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/golf/leaderboard/${eventId}?event_kind=${eventKind}`);
      setData(res);
    } catch { /* silent — event may have no scorecards yet */ }
    finally { setLoading(false); }
  }, [eventId, eventKind]);

  useEffect(() => { load(); }, [load, refreshSignal]);
  useEffect(() => {
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      load();
    }, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <div className="pp-card p-6 text-center text-zinc-500 text-sm">Loading leaderboard…</div>;
  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <div className="pp-card p-6 text-center text-zinc-500 text-sm" data-testid="golf-leaderboard-empty">
        <Trophy size={24} className="mx-auto mb-2 text-zinc-700" />
        No cards submitted yet. Be the first.
      </div>
    );
  }

  const fmt = data.format || "stroke";
  const isStableford = fmt === "stableford";
  const showTeams = (data.teams || []).length > 0;

  return (
    <section className="pp-card p-4" data-testid="golf-leaderboard">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy size={18} className="text-[#00E676]" />
          <h3 className="font-heading text-lg font-black">Leaderboard</h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400 uppercase tracking-wider">{fmt.replace(/_/g, " ")}</span>
        </div>
        <span className="text-[10px] text-zinc-500">Live · refreshes every 30s</span>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
              <th className="p-2 text-left w-12">Pos</th>
              <th className="p-2 text-left">Player</th>
              <th className="p-2 text-center">Holes</th>
              <th className="p-2 text-center">{isStableford ? "Pts" : "Gross"}</th>
              <th className="p-2 text-center">vs Par</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, idx) => {
              const t = r.totals || {};
              const over = (t.gross_strokes || 0) - (data.course_par || 72) * (t.holes_played / (data.holes || 18));
              const overFmt = !t.gross_strokes ? "—" : (over > 0 ? `+${Math.round(over)}` : over < 0 ? Math.round(over) : "E");
              return (
                <tr key={r.player_id} data-testid={`gl-row-${idx}`} className="border-t border-white/5">
                  <td className="p-2 text-left text-zinc-400 font-bold">
                    {idx === 0 ? <Medal size={14} className="text-[#FFD60A] inline" /> : idx === 1 ? <Medal size={14} className="text-zinc-400 inline" /> : idx === 2 ? <Medal size={14} className="text-[#CD7F32] inline" /> : idx + 1}
                  </td>
                  <td className="p-2 text-left">
                    <div className="font-bold truncate">{r.player_name}</div>
                    {r.team_id && <div className="text-[10px] text-zinc-500">Team {r.team_id}</div>}
                  </td>
                  <td className="p-2 text-center text-zinc-400">{t.holes_played || 0}</td>
                  <td className="p-2 text-center font-black text-[#00E676]">{isStableford ? (t.stableford_points ?? 0) : (t.gross_strokes || "—")}</td>
                  <td className="p-2 text-center text-zinc-300">{overFmt}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showTeams && (
        <div className="mt-6 pt-4 border-t border-white/10" data-testid="golf-teams-board">
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-[#00E676]" />
            <h4 className="font-heading font-black">Teams</h4>
          </div>
          <div className="space-y-1">
            {data.teams.map((tm, i) => (
              <div key={tm.team_id} className="flex items-center justify-between text-sm py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 font-bold w-6">{i + 1}</span>
                  <span className="font-bold">Team {tm.team_id}</span>
                  <span className="text-xs text-zinc-500">({tm.players.join(" · ")})</span>
                </div>
                <span className="font-black text-[#00E676]">{isStableford ? `${tm.stableford_points} pts` : `${tm.gross_strokes} strokes`}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
